import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Slide as SlideAnimation } from 'react-awesome-reveal';
import { HeaderV6 } from '../HeaderV6';
import { Button } from '../Button';
import { QuizProgressBar } from '../QuizProgressBar';
import { QuestionAnswerSlideV6 } from '../QuestionAnswerSlideV6';
import { QuestionScaleSlideV2 } from '../QuestionScaleSlideV2';
import { StartSlideV14 } from '../StartSlideV14';
import { PauseSlide } from '../PauseSlide';
import { ProductSlide } from '../ProductSlide';
import { ProfileSlideV4 } from '../ProfileSlideV4';
import { PlanLoaderSlideV8 } from '../PlanLoaderSlideV8';
import { EmailSlideV7 } from '../EmailSlideV7';
import { ResultSlideV2 } from '../ResultSlideV2';
import { PlanGraphSlideV8 } from '../PlanGraphSlideV8';
import { SaleV35 } from '../SaleV35';
import { FooterV2 } from '../FooterV2';
import { AgreementTextV4 } from '../AgreementTextV4';
import { useQuiz } from '../../shared/use-quiz';
import { useUserProperties } from '../../shared/use-user-properties';
import { useLegalInfo } from '../../shared/use-legal-info';
import { useSlideEvents } from '../../shared/use-slide-events';
import {
    EMAIL_SLIDE,
    PAUSE_SLIDE,
    PLAN_GRAPH_SLIDE,
    PLAN_LOADER_SLIDE,
    PRODUCT_SLIDE,
    PROFILE_SLIDE,
    QUESTION_ANSWER_SLIDE_COMPONENT,
    QUESTION_SCALE_SLIDE_COMPONENT,
    RESULT_SLIDE,
    START_SLIDE
} from '../../shared/quiz-component-types';
import { preloadImages } from '../../../lib/shared/preload-images';
import emojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v56.png';
import resultEmojiSpriteSrc from '../../assets/images/emoji-sprites/emoji-sprite-v56-result.png';
import pauseTwoWomenTabletSrc from '../../assets/images/slide/pause-two-women-tablet.png';
import pauseLaptopChatSrc from '../../assets/images/slide/pause-laptop-chat.png';
import greenWaveSrc from '../../assets/images/slide/green-wave.png';
import pauseChaptersLaptopSrc from '../../assets/images/slide/pause-chapters-laptop.png';
import pausePhoneQuestionsSrc from '../../assets/images/slide/pause-phone-questions.png';
import productBookPreviewTabletSrc from '../../assets/images/slide/product-book-preview-tablet.png';
import footerLogoSrc from '../../assets/images/boundly-logo-green-light.png';
import styles from './CommonPrelanderV48.css';
import '../../assets/images/emoji-sprites/emoji-sprite-v56.css';

const cx = classnames.bind(styles);

const IMAGES = [
    emojiSpriteSrc,
    resultEmojiSpriteSrc,
    pauseTwoWomenTabletSrc,
    pauseLaptopChatSrc,
    greenWaveSrc,
    pauseChaptersLaptopSrc,
    pausePhoneQuestionsSrc,
    productBookPreviewTabletSrc
];

const SLIDE_COMPONENTS = {
    [QUESTION_ANSWER_SLIDE_COMPONENT]: QuestionAnswerSlideV6,
    [QUESTION_SCALE_SLIDE_COMPONENT]: QuestionScaleSlideV2,
    [START_SLIDE]: StartSlideV14,
    [PAUSE_SLIDE]: PauseSlide,
    [PRODUCT_SLIDE]: ProductSlide,
    [PROFILE_SLIDE]: ProfileSlideV4,
    [PLAN_LOADER_SLIDE]: PlanLoaderSlideV8,
    [EMAIL_SLIDE]: EmailSlideV7,
    [RESULT_SLIDE]: ResultSlideV2,
    [PLAN_GRAPH_SLIDE]: PlanGraphSlideV8
};

const DELAY = 350;

function CommonPrelanderV48({
    initialPrelander,
    quizSlides
}) {
    const {
        currentSlideIndex,
        currentSlide,
        results,
        submitAnswer,
        submitTemporaryAnswer,
        temporaryAnswer,
        finished,
        nextSlide,
        backSlide,
        setEmail
    } = useQuiz(quizSlides, initialPrelander);

    const quizSlidesIndexing = quizSlides.filter(slide => !slide.isNotIndexing);
    const isQuestionSlide = [QUESTION_ANSWER_SLIDE_COMPONENT, QUESTION_SCALE_SLIDE_COMPONENT].includes(currentSlide.component);
    const isStartSlide = currentSlide.component === START_SLIDE;
    const isEmailSlide = currentSlide.component === EMAIL_SLIDE;
    const indexedSlideNumber = quizSlidesIndexing.indexOf(currentSlide) + 1;
    const { legalLinks, legalEntity } = useLegalInfo();
    const [selectedSubscriptionData, selectSubscription] = useState({});
    const [merchantData, setMerchantData] = useState({});

    useUserProperties(initialPrelander);

    useEffect(() => {
        preloadImages(IMAGES);
    }, []);

    const { logQuizLifecycle } = useSlideEvents({ currentSlideIndex: indexedSlideNumber, results, currentSlide, quizSlides });

    const handleAnswerWithTransition = answer => () => {
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, answer);
        submitAnswer(answer);
    };

    const handleContinue = () => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });
        logQuizLifecycle(quizSlidesIndexing.length === indexedSlideNumber, temporaryAnswer);

        nextSlide(temporaryAnswer);
    };

    const renderHeader = () => {
        const hasBackButton = !finished && Boolean(currentSlideIndex) && !currentSlide?.isNotBackButton;

        return (
            <HeaderV6
                progressBar={isQuestionSlide && (
                    <QuizProgressBar
                        currentSlideIndex={indexedSlideNumber}
                        totalSlides={quizSlidesIndexing.length}
                    />
                )}
                questionNav={isQuestionSlide && {
                    category: currentSlide.props?.category,
                    hasCounter: true,
                    currentSlideIndex: indexedSlideNumber,
                    totalSlides: quizSlidesIndexing.length
                }}
                hasBackButton={hasBackButton}
                onBack={backSlide}
                className={cx('header', { withStartSlide: isStartSlide })}
                isFinished={finished && Boolean(results.email) && !selectedSubscriptionData.plan}
                url={window.location.pathname}
            />
        );
    };

    const renderFooter = () => {
        if (finished) {
            if (Boolean(results.email) && !selectedSubscriptionData.plan) {
                return <FooterV2 legalEntity={legalEntity} legalLinks={legalLinks} logoSrc={footerLogoSrc} />;
            }

            return null;
        }

        if (Boolean(currentSlide.continueButton)) {
            return (
                <SlideAnimation
                    delay={500}
                    duration={250}
                    triggerOnce
                    direction='up'
                    className={styles.slideFooter}
                    key={currentSlide.id}
                >
                    <div className={styles.slideFooterWrapper}>
                        <Button
                            type='pine'
                            label={currentSlide.continueButton.label || 'Continue'}
                            onClick={handleContinue}
                            inSlide
                        />
                    </div>
                </SlideAnimation>
            );
        }

        return null;
    };

    const renderContent = () => {
        if (finished) {
            return (
                <SaleV35
                    data={results}
                    initialPrelander={initialPrelander}
                    selectSubscription={selectSubscription}
                    selectedSubscriptionData={selectedSubscriptionData}
                    merchantData={merchantData}
                    setMerchantData={setMerchantData}
                    legalLinks={legalLinks}
                    legalEntity={legalEntity}
                />
            );
        }

        const Slide = SLIDE_COMPONENTS[currentSlide.component];
        const resultData = (temporaryAnswer?.id || temporaryAnswer?.answerIds)
            ? temporaryAnswer
            : results.userResponses.find(userResponse => userResponse.questionId === currentSlide.id);

        return (
            <div
                key={currentSlideIndex}
                className={cx('slide', {
                    withQuestion: isQuestionSlide,
                    withStartSlide: isStartSlide,
                    withEmailSlide: isEmailSlide,
                    withPlanLoaderSlide: currentSlide.component === PLAN_LOADER_SLIDE,
                    withProfileSlide: currentSlide.component === PROFILE_SLIDE,
                    withScaleQuestion: currentSlide.component === QUESTION_SCALE_SLIDE_COMPONENT
                })}
            >
                <Slide
                    {...currentSlide.props}
                    spriteVersion='v56'
                    submitAnswer={handleAnswerWithTransition}
                    submitTemporaryAnswer={submitTemporaryAnswer}
                    userResponse={resultData}
                    onContinue={handleContinue}
                    id={currentSlide.id}
                    results={results}
                    delay={DELAY}
                    setEmail={setEmail}
                    legalLinks={legalLinks}
                />
                {isStartSlide && <AgreementTextV4 legalLinks={legalLinks} />}
            </div>
        );
    };

    return (
        <div className={cx('container', { withStartSlide: isStartSlide })}>
            <div className={cx({ stickyHeader: finished && Boolean(results.email) && !selectedSubscriptionData.plan })}>
                {renderHeader()}
            </div>
            <main
                key={finished}
                className={cx('main', {
                    centered: currentSlide.centered,
                    animated: finished || isEmailSlide
                })}
            >
                {renderContent()}
            </main>
            {renderFooter()}
        </div>
    );
}

export {
    CommonPrelanderV48
};
